import { environment } from '../../environments/environment';
import {
  ActionReducer,
  MetaReducer,
} from '@ngrx/store';
import * as fromPropertyDetailsReducers from '@properties/reducers/property-details';
import * as fromUnitDetailsReducers from '@units/reducers/unit-details';
import * as fromOutstandingDebtReducers from '@outstanding-debts/reducers/outstanding-debt';
import * as fromAuthReducers from '@auth/reducers/auth';
import * as fromUsersReducers from '@users/reducers/users';
import * as fromClientDetailsReducers from '@clients/reducers/client-details';
import * as fromEstateDetailsReducers from "@estates/reducers/estate-details";
import * as fromClientParentsReducers from '@client-parents/reducers/client-parents';
import * as fromOverviewReducers from '@overview/reducers/overview';
import * as fromHeadleaseDetailsReducers from '@headleases/reducers/headlease-details';
import * as fromUserSettingsReducers from '@user-settings/reducers/user-settings';
import * as fromSoftLabelsReducers from '@soft-labels/reducers/soft-labels';
import * as fromLeaseDetailsReducers from '@leases/reducers/lease-details';
import * as fromDashboardReducers from '@dashboards/reducers/dashboard-reducers';

import * as fromPropertyPictureReducers from '@properties/reducers/property-picture';
import * as fromDiaryReducers from '@diary/reducers/diary-reducer';
import * as fromAboutReducers from '@about/reducers/about.reducers';
import * as fromTenancyScheduleReducers from '@tenancy-schedule/reducers/tenancy-schedule-table';
import * as fromTenancyScheduleColumnsReducers from '@tenancy-schedule/reducers/tenancy-schedule-table-columns';
import * as fromContactUsReducers from '@contact-us/reducers/contactus';
import * as fromWidgetGalleryReducers from '@widget-gallery/reducers/widget-gallery.reducers';
import * as fromCashBalancesReducers from '@cash-balances/reducers/cash-balances.reducers';
import * as fromWidgetConfigReducers from '@widget-configs/reducers/widget-configs';
import * as fromProfileReducers from '@profiles/reducers/profiles';
import * as fromDashboardDesignerReducers from '@dashboard-designer/reducers/dashboard-designer.reducers';
import * as fromFiltersReducers from '@common/filter/reducers/filter.reducers';

import * as fromUserProfilesReducers from '@user-profiles/reducers/user-profiles';
import * as fromLandlordTableReducers from '@landlords/reducers/landlord-table';
import * as fromLandlordTableColumnsReducers from '@landlords/reducers/landlord-table-columns';
import * as fromEmailTemplateReducers from '@admin/email-templates/reducers/email-template.reducers';
import * as fromSystemConfigurationReducers from '@admin/reducers/system-configuration.reducer';
import * as fromLandlordDetailsReducers from '@landlords/reducers/landlord-details.reducers';
import * as fromClientParentDetailsReducers from '@client-parents/reducers/client-parent-details.reducers';
import * as fromTenantDetailsReducers from '@tenants/reducers/tenant-details.reducers';
import * as fromSupplierDetailsReducers from '@suppliers/reducers/supplier-details.reducers';
import * as fromDebtorDetailsReducers from '@debtors/reducers/debtor-details.reducers';
import * as fromManagerDetailsReducers from '@manager-details/reducers/manager-details.reducers';
import * as fromInvoiceUploadReducers from '@invoice-upload/reducers/invoice-upload.reducers';
import * as fromPaymentTableReducers from '@payments-to-you/reducers/payments-to-you-table.reducers';
import * as fromSupplierOtherCreditorReducers from '@feature/suppliers/reducers/supplier-other-creditor.reducers';
import * as fromPaymentTableColumnReducers from '@payments-to-you/reducers/payments-to-you-table-columns.reducers';
import * as fromDashboardConfigReducers from '@dashboard-widget/reducers/dashboard-widget-config.reducers';
import * as fromEntityKeyServiceReducers from '@pma-entity/reducers/entity-key.reducers';
import * as fromUserPreferencesReducers from '@feature/user-preferences/reducers/user-preferences';
import * as fromBudgetVsActualReducers from '@feature/_app_modules/budget-vs-actual/reducers/budget-vs-actual.reducers';

export interface ApplicationState {
  propertyDetails: fromPropertyDetailsReducers.State;
  unitDetails: fromUnitDetailsReducers.State;
  outstandingDebt: fromOutstandingDebtReducers.State;
  auth: fromAuthReducers.State;
  users: fromUsersReducers.State;
  clientDetails: fromClientDetailsReducers.State;
  estateDetails: fromEstateDetailsReducers.State;
  clientParents: fromClientParentsReducers.State;
  userSettings: fromUserSettingsReducers.State;
  softLabels: fromSoftLabelsReducers.State;
  overview: fromOverviewReducers.State;
  leaseDetails: fromLeaseDetailsReducers.State;
  headleaseDetails: fromHeadleaseDetailsReducers.State;
  dashboard: fromDashboardReducers.DashboardState;

  propertyPictures: fromPropertyPictureReducers.State;
  about: fromAboutReducers.State;
  tenancySchedule: fromTenancyScheduleReducers.State;
  tenancyScheduleAvailableColumns: fromTenancyScheduleColumnsReducers.State;
  contactInfo: fromContactUsReducers.State;
  widgetGallery: fromWidgetGalleryReducers.State;
  cashBalances: fromCashBalancesReducers.CashBalancesState;
  diaryWidgetData: fromDiaryReducers.DiaryWidgetDataState;
  widgetConfig: fromWidgetConfigReducers.State;
  profiles: fromProfileReducers.State;
  dashboardDesigner: fromDashboardDesignerReducers.DashboardDesignerState;
  widgetFilters: fromFiltersReducers.WidgetFilterState;
  listTypeFilters: fromFiltersReducers.ListTypeFilterState;

  userProfiles: fromUserProfilesReducers.State;
  landlordTable: fromLandlordTableReducers.State;
  landlordTableAvailableColumns: fromLandlordTableColumnsReducers.State;
  emailTemplates: fromEmailTemplateReducers.State;
  systemConfiguration: fromSystemConfigurationReducers.State;
  landlordDetails: fromLandlordDetailsReducers.State;
  tenantDetails: fromTenantDetailsReducers.State;
  supplierDetails: fromSupplierDetailsReducers.State;
  clientParentDetails: fromClientParentDetailsReducers.State;
  debtorDetails: fromDebtorDetailsReducers.State;
  managerDetails: fromManagerDetailsReducers.State;
  invoiceUploads: fromInvoiceUploadReducers.State;
  paymentTable: fromPaymentTableReducers.State;
  supplierOtherCreditors: fromSupplierOtherCreditorReducers.State;
  paymentTableColumn: fromPaymentTableColumnReducers.State;
  dashboardWidgetConfiguration: fromDashboardConfigReducers.State;
  entityKey: fromEntityKeyServiceReducers.State;

  userPreferences: fromUserPreferencesReducers.State;
  budgetVsActual: fromBudgetVsActualReducers.State;
}

export function getReducers() {
  return {
    propertyDetails: fromPropertyDetailsReducers.reducer,
    unitDetails: fromUnitDetailsReducers.reducer,
    outstandingDebt: fromOutstandingDebtReducers.reducer,
    auth: fromAuthReducers.reducer,
    users: fromUsersReducers.reducer,
    clientDetails: fromClientDetailsReducers.reducer,
    estateDetails: fromEstateDetailsReducers.reducer,
    clientParents: fromClientParentsReducers.reducer,
    userSettings: fromUserSettingsReducers.reducer,
    softLabels: fromSoftLabelsReducers.reducer,
    overview: fromOverviewReducers.reducer,
    leaseDetails: fromLeaseDetailsReducers.reducer,
    headleaseDetails: fromHeadleaseDetailsReducers.reducer,
    dashboard: fromDashboardReducers.dashboardReducer,
    propertyPictures: fromPropertyPictureReducers.reducer,

    about: fromAboutReducers.reducer,
    tenancySchedule: fromTenancyScheduleReducers.reducer,
    tenancyScheduleAvailableColumns: fromTenancyScheduleColumnsReducers.reducer,
    contactInfo: fromContactUsReducers.reducer,
    widgetGallery: fromWidgetGalleryReducers.reducer,
    cashBalances: fromCashBalancesReducers.reducer,
    diaryWidgetData: fromDiaryReducers.reducer,
    widgetConfig: fromWidgetConfigReducers.reducer,
    profiles: fromProfileReducers.reducer,
    dashboardDesigner: fromDashboardDesignerReducers.reducer,
    widgetFilters: fromFiltersReducers.widgetFilterReducer,
    listTypeFilters: fromFiltersReducers.listTypeFilterReducer,

    userProfiles: fromUserProfilesReducers.reducer,
    landlordTable: fromLandlordTableReducers.reducer,
    landlordTableAvailableColumns: fromLandlordTableColumnsReducers.reducer,
    emailTemplates: fromEmailTemplateReducers.reducer,
    systemConfiguration: fromSystemConfigurationReducers.reducer,
    landlordDetails: fromLandlordDetailsReducers.reducer,
    clientParentDetails: fromClientParentDetailsReducers.reducer,
    supplierDetails: fromSupplierDetailsReducers.reducer,
    debtorDetails: fromDebtorDetailsReducers.reducer,
    tenantDetails: fromTenantDetailsReducers.reducer,
    managerDetails: fromManagerDetailsReducers.reducer,
    invoiceUploads: fromInvoiceUploadReducers.reducer,
    paymentTable: fromPaymentTableReducers.reducer,
    supplierOtherCreditors: fromSupplierOtherCreditorReducers.reducer,
    paymentTableColumn: fromPaymentTableColumnReducers.reducer,
    dashboardWidgetConfiguration: fromDashboardConfigReducers.reducer,
    entityKey: fromEntityKeyServiceReducers.reducer,
    userPreferences: fromUserPreferencesReducers.reducer,
    budgetVsActual: fromBudgetVsActualReducers.reducer
  };
}

// log all actions
export function logger(reducer: ActionReducer<ApplicationState>): ActionReducer<ApplicationState> {
  return (state: ApplicationState, action: any): ApplicationState => {
    console.log('state', state);
    console.log('action', action.type, action);
    return reducer(state, action);
  };
}

export const metaReducers: MetaReducer<ApplicationState>[] = !environment.production
  ? [logger]
  : [];
